<template>
  <div class="session-management-page">
    <router-link to="/" class="back-link">&larr; Back to Homepage</router-link>
    <h1>Manage Sessions</h1>
    <button class="add-session-button" @click="openAddForm">
      <svg class="add-icon" viewBox="0 0 24 24" fill="currentColor">
        <path d="M19 13h-6v6h-2v-6H5v-2h6V5h2v6h6v2z"/>
      </svg>
      Add New Session
    </button>
    <LoadingSpinner v-if="isLoading" size="medium" message="Loading sessions..." />
    <div v-if="error" class="error">{{ error }}</div>
    <div v-if="!isLoading && sessionsList.length === 0" class="empty-state">
      <svg class="empty-icon" viewBox="0 0 24 24" fill="currentColor">
        <path d="M19 3h-1V1h-2v2H8V1H6v2H5c-1.11 0-1.99.9-1.99 2L3 19c0 1.1.89 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm0 16H5V8h14v11zM7 10h5v5H7z"/>
      </svg>
      <h3>No sessions found</h3>
      <p>Get started by creating your first review session!</p>
    </div>
    <div v-if="sessionsList.length > 0" class="sessions-grid">
      <SessionListItem
        v-for="session in sessionsList"
        :key="session.id"
        :session="session"
      />
    </div>
    <SessionForm
      v-if="isFormOpen"
      :initialData="editingSession"
      :mode="formMode"
      :adminPassword="adminPassword"
      @submit="handleFormSubmit"
      @cancel="closeForm"
      :isLoading="isLoading"
    />
  </div>
</template>

<script setup>
import { onMounted } from 'vue';
import useSessionManagement from '../composables/useSessionManagement';
import SessionListItem from '../components/SessionListItem.vue';
import SessionForm from '../components/SessionForm.vue';
import LoadingSpinner from '../components/LoadingSpinner.vue';

const {
  sessionsList,
  isLoading,
  error,
  editingSession,
  isFormOpen,
  formMode,
  adminPassword,
  fetchSessions,
  openAddForm,
  closeForm,
  createSession
} = useSessionManagement();

onMounted(fetchSessions);

function handleFormSubmit(formData) {
  if (formMode.value === 'add') {
    createSession(formData);
  }
}
</script>

<style scoped>
.session-management-page {
  max-width: 1400px;
  margin: 2rem auto;
  background: #292929;
  padding: 2rem;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
}
h1 {
  font-size: 2.25rem;
  font-weight: 600;
  color: #ffffff;
  margin-bottom: 2rem;
  letter-spacing: -0.02em;
}
.add-session-button {
  display: flex;
  align-items: center;
  margin-bottom: 2rem;
  padding: 0.875rem 1.5rem;
  background: #2d8cf0;
  color: #fff;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  font-weight: 600;
  font-size: 0.95rem;
  transition: all 0.2s ease;
  box-shadow: 0 2px 4px rgba(45, 140, 240, 0.2);
}

.add-session-button:hover {
  background: #1867c0;
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(45, 140, 240, 0.3);
}

.add-session-button:active {
  transform: translateY(0);
}

.add-icon {
  width: 18px;
  height: 18px;
  margin-right: 0.5rem;
}
.error {
  color: #ff5252;
  margin-bottom: 1rem;
}
.sessions-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(700px, 1fr));
  gap: 2rem;
  margin-top: 1.5rem;
}
.back-link {
  display: block;
  margin-bottom: 1rem;
  color: #fff;
  text-decoration: none;
}

.empty-state {
  text-align: center;
  padding: 3rem 2rem;
  color: #b0b0b0;
}

.empty-icon {
  width: 48px;
  height: 48px;
  color: #666;
  margin-bottom: 1rem;
}

.empty-state h3 {
  color: #ffffff;
  font-size: 1.25rem;
  font-weight: 600;
  margin-bottom: 0.5rem;
}

.empty-state p {
  font-size: 0.95rem;
  line-height: 1.5;
}

/* Large screens - more columns for wider space utilization */
@media (min-width: 1400px) {
  .sessions-grid {
    grid-template-columns: repeat(auto-fit, minmax(800px, 1fr));
    gap: 2.5rem;
  }
}

/* Extra large screens - ensure good space utilization */
@media (min-width: 1800px) {
  .session-management-page {
    max-width: 1800px;
  }

  .sessions-grid {
    grid-template-columns: repeat(auto-fit, minmax(900px, 1fr));
    gap: 3rem;
  }
}

/* Tablet and smaller desktop */
@media (max-width: 768px) {
  .session-management-page {
    margin: 1rem;
    padding: 1.5rem;
  }
  
  .sessions-grid {
    grid-template-columns: 1fr;
    gap: 1rem;
  }
}

/* Mobile */
@media (max-width: 600px) {
  .session-management-page {
    margin: 0.5rem;
    padding: 1rem;
  }
  
  .sessions-grid {
    grid-template-columns: 1fr;
    gap: 1rem;
  }
}
</style> 